import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import type { ISupplier } from '@bridebook/models/source/models/Suppliers.types';
import type { ISupplierAdmin } from '@bridebook/models/source/models/Suppliers/Admin.types';
import type { IOffer } from '@bridebook/models/source/models/Suppliers/Offers.types';
import { IQuestion } from '@bridebook/models/source/models/Suppliers/Questions.types';
import { slugify } from '@bridebook/toolbox';
import { Accordion, Box } from '@bridebook/ui';
import { FAQ_SECTION_ID } from '@bridebook/web/app-shared/components/supplier/sections/ids';
import SectionHeading from '@bridebook/web/app-shared/components/supplier/sections/section-heading';
import SectionText from '@bridebook/web/app-shared/components/supplier/sections/section-text/section-text';
import hasBenefits from '@bridebook/web/app-shared/lib/supplier/utils/has-benefits';
import componentStyles from '@bridebook/web/app-basic/components/supplier/sections/faq/faq.style';
import { buildFaqs } from '@bridebook/web/app-shared/lib/supplier/utils/build-faqs';
import { renderHtml } from '@bridebook/web/app-shared/lib/ui/html-renderer';

interface IProps {
  supplier: ISupplier;
  premium: ISupplierAdmin['premium'] | null;
  offers: IOffer[] | null | undefined;
  questions: IQuestion[];
  isLoggedIn?: boolean;
  onToggleAccordion?: (isOpen: boolean) => void;
}

const Faq = ({ supplier, premium, offers, questions, isLoggedIn, onToggleAccordion }: IProps) => {
  const { t } = useTranslation('supplier');

  const isBenefits = hasBenefits({ supplier, offers, premium: premium || undefined });
  const faqs = useMemo(() => buildFaqs(questions), [questions]);

  if (faqs.length === 0) return null;

  const styles = componentStyles({ isBenefits });

  return (
    <Box style={styles.accordionWrapper} id={FAQ_SECTION_ID}>
      <Accordion
        noRoundBorder
        noTopBorder
        arrowStartDown
        noBgColor
        openStatic={!isLoggedIn}
        noSidePaddings
        onToggle={onToggleAccordion}
        text={
          <Box>
            <Box style={styles.faqTitle}>{t('supplier.accordion.faq.title')}</Box>
            <Box style={styles.faqDescription}>{t('supplier.accordion.faq.description')}</Box>
          </Box>
        }>
        <Box mt={4} mb={4}>
          <Box style={styles.questionsWrap}>
            {faqs.map((faq) => (
              <Box style={styles.faqItem} key={`faq-item-${slugify(faq.question)}`}>
                <SectionHeading>{faq.question}</SectionHeading>
                <SectionText>{renderHtml(faq.answer)}</SectionText>
              </Box>
            ))}
          </Box>
        </Box>
      </Accordion>
    </Box>
  );
};

export default Faq;
